import React from "react";
import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import Checkbox from "@mui/material/Checkbox";
import Tooltip from "@mui/material/Tooltip";
import CloseIcon from "@mui/icons-material/Close";
import GroupAddIcon from "@mui/icons-material/GroupAdd";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { describeDefaultDueDate } from "./dueDateHelpers";

// Bulk-assign a single track to many team members from the By Employee matrix
// header. Members already on the track are shown locked (their progress is
// never touched); only newly checked members are enrolled. The write is
// store-scoped to whoever is passed in via `teamMembers`.
function BulkAssignTrackModal(props) {
  const { track, teamMembers } = props;

  const [selectedIds, setSelectedIds] = React.useState(() => new Set());
  const [selectedDueDate, setSelectedDueDate] = React.useState(null);

  // Split the roster into who can be assigned vs who is already enrolled in
  // this track (matches getEnrollmentForTrack in TrainingStoreDisplay).
  const { assignableMembers, alreadyEnrolledMembers } = React.useMemo(() => {
    const assignable = [];
    const enrolled = [];
    (teamMembers || []).forEach((member) => {
      const isEnrolled = (
        member?.user?.learning_track_enrollments || []
      ).some((enrollment) => enrollment?.track?.uuid === track?.uuid);
      (isEnrolled ? enrolled : assignable).push(member);
    });
    return {
      assignableMembers: assignable,
      alreadyEnrolledMembers: enrolled,
    };
  }, [teamMembers, track]);

  // Default: every assignable member checked. Re-applied each time the modal
  // opens or the target track changes.
  React.useEffect(() => {
    if (props.open) {
      setSelectedIds(new Set(assignableMembers.map((member) => member.id)));
      setSelectedDueDate(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.open, track?.uuid]);

  const style = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: 600,
    maxHeight: "90vh",
    bgcolor: "background.paper",
    borderRadius: "16px",
    boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
    overflowY: "auto",
    border: "none",
  };

  const toggleMember = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const allAssignableSelected =
    assignableMembers.length > 0 &&
    selectedIds.size === assignableMembers.length;

  const toggleSelectAll = () => {
    if (allAssignableSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(assignableMembers.map((member) => member.id)));
    }
  };

  const handleClose = () => {
    setSelectedIds(new Set());
    setSelectedDueDate(null);
    props.handleClose();
  };

  const handleConfirm = () => {
    if (selectedIds.size === 0) return;
    // Format date as YYYY-MM-DD string (same format as track creation; avoids
    // the timezone shift toISOString would introduce).
    const dueDateString = selectedDueDate
      ? `${selectedDueDate.getFullYear()}-${String(
          selectedDueDate.getMonth() + 1
        ).padStart(2, "0")}-${String(selectedDueDate.getDate()).padStart(
          2,
          "0"
        )}`
      : null;

    props.post_bulk_learning_track_enrollment(
      track?.uuid,
      Array.from(selectedIds),
      dueDateString
    );
    handleClose();
  };

  const memberInitials = (member) =>
    `${member?.first_name?.[0] || ""}${
      member?.last_name?.[0] || ""
    }`.toUpperCase();

  const memberName = (member) =>
    `${member?.first_name || ""} ${member?.last_name || ""}`.trim();

  return (
    <Modal
      open={props.open}
      onClose={handleClose}
      aria-labelledby="bulk-assign-track-modal-title"
      aria-describedby="bulk-assign-track-modal-description"
    >
      <Box sx={style}>
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 mx-0 mt-0 mb-6 z-10">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-violet-100 rounded-lg flex items-center justify-center">
                <GroupAddIcon
                  className="text-violet-600"
                  style={{ fontSize: "22px" }}
                />
              </div>
              <div>
                <h3 className="text-2xl font-bold text-slate-900">
                  Assign Team Members
                </h3>
                <p className="text-sm text-slate-600">
                  Assign{" "}
                  <span className="font-semibold text-slate-700">
                    {track?.name || "this item"}
                  </span>{" "}
                  to multiple team members at once
                </p>
              </div>
            </div>
            <button
              className="w-8 h-8 rounded-lg hover:bg-gray-100 flex items-center justify-center text-gray-500 hover:text-gray-700 transition-colors"
              onClick={handleClose}
            >
              <CloseIcon />
            </button>
          </div>
        </div>

        <div className="px-6 space-y-6 pb-2">
          {/* Member selection */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-1.5">
                <label className="block text-sm font-medium text-gray-700">
                  Team Members
                </label>
                <Tooltip
                  title="Team members already assigned keep their progress, only the members you select below will be newly assigned."
                  arrow
                >
                  <InfoOutlinedIcon
                    className="text-gray-400 hover:text-gray-600 cursor-help"
                    style={{ fontSize: "16px" }}
                  />
                </Tooltip>
              </div>
              {assignableMembers.length > 0 && (
                <button
                  className="text-sm font-medium text-violet-600 hover:text-violet-700 transition-colors"
                  onClick={toggleSelectAll}
                >
                  {allAssignableSelected ? "Deselect all" : "Select all"}
                </button>
              )}
            </div>
            <p className="text-sm text-gray-500 mb-3">
              {assignableMembers.length > 0
                ? `Assigning to ${selectedIds.size} of ${assignableMembers.length} available team members.`
                : "All team members shown are already assigned this."}
            </p>

            <div className="border border-gray-200 rounded-lg divide-y divide-gray-100 max-h-80 overflow-y-auto">
              {/* Assignable members */}
              {assignableMembers.map((member) => {
                const checked = selectedIds.has(member.id);
                return (
                  <button
                    key={member.id}
                    type="button"
                    onClick={() => toggleMember(member.id)}
                    className="w-full flex items-center gap-3 px-3 py-2.5 text-left hover:bg-gray-50 transition-colors"
                  >
                    <Checkbox
                      checked={checked}
                      tabIndex={-1}
                      size="small"
                      sx={{
                        p: 0,
                        color: "#c4b5fd",
                        "&.Mui-checked": { color: "#6d28d9" },
                      }}
                    />
                    <div className="w-8 h-8 rounded-full bg-violet-100 flex items-center justify-center text-xs font-semibold text-violet-700 shrink-0">
                      {memberInitials(member)}
                    </div>
                    <span className="text-sm font-medium text-gray-900">
                      {memberName(member)}
                    </span>
                  </button>
                );
              })}

              {/* Already-enrolled members: shown, locked, never written */}
              {alreadyEnrolledMembers.map((member) => (
                <div
                  key={member.id}
                  className="w-full flex items-center gap-3 px-3 py-2.5 bg-gray-50"
                >
                  <Checkbox
                    checked
                    disabled
                    tabIndex={-1}
                    size="small"
                    sx={{ p: 0 }}
                  />
                  <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-xs font-semibold text-gray-500 shrink-0">
                    {memberInitials(member)}
                  </div>
                  <span className="text-sm font-medium text-gray-500 flex-1">
                    {memberName(member)}
                  </span>
                  <span className="text-xxs font-medium text-gray-400 uppercase tracking-wide">
                    Already assigned
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Shared due date */}
          <div>
            <div className="flex items-center gap-2 mb-2">
              <CalendarTodayIcon
                className="text-gray-500"
                style={{ fontSize: "18px" }}
              />
              <label className="block text-sm font-medium text-gray-700">
                Custom Due Date (Optional)
              </label>
            </div>
            <p className="text-sm text-gray-500 mb-3">
              {(() => {
                const desc = describeDefaultDueDate(track);
                return desc
                  ? `${desc} Set a custom date to override it for everyone you assign now.`
                  : "This has no default due date. Add one here to give everyone you assign a deadline.";
              })()}
            </p>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                value={selectedDueDate}
                onChange={(newDate) => setSelectedDueDate(newDate)}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    size: "small",
                    placeholder: "Select a custom due date",
                    sx: {
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "8px",
                        height: "45px",
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#6d28d9",
                        },
                      },
                    },
                  },
                  actionBar: {
                    actions: ["clear", "accept"],
                  },
                }}
              />
            </LocalizationProvider>
          </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-white border-t border-gray-200 px-6 py-4 mx-0 mb-0 mt-8 z-10">
          <div className="flex items-center justify-end gap-3">
            <button
              className="px-6 py-2.5 border border-gray-300 hover:bg-gray-50 text-gray-700 rounded-lg font-medium transition-colors"
              onClick={handleClose}
            >
              Cancel
            </button>
            <button
              className={`px-6 py-2.5 rounded-lg font-medium transition-colors ${
                selectedIds.size > 0
                  ? "bg-violet-600 hover:bg-violet-700 text-white"
                  : "bg-gray-200 text-gray-400 cursor-not-allowed"
              }`}
              onClick={handleConfirm}
              disabled={selectedIds.size === 0}
            >
              {selectedIds.size > 0
                ? `Assign to ${selectedIds.size} ${
                    selectedIds.size === 1 ? "member" : "members"
                  }`
                : "Assign"}
            </button>
          </div>
        </div>
      </Box>
    </Modal>
  );
}

export default BulkAssignTrackModal;
